import { ChevronLeft, Target, Flame, Trophy } from 'lucide-react';
import { Link } from 'react-router';
import { PacienteBottomNav } from '@/app/components/paciente/PacienteBottomNav';
import { PacienteSidebar } from '@/app/components/paciente/PacienteSidebar';
import { PacienteHeaderDesktop } from '@/app/components/paciente/PacienteHeaderDesktop';
import {
  useProgresoPaciente,
  type ProgresoPacienteData,
} from '@/lib/paciente/useProgresoPaciente';
import type { AdherenciaResult } from '@/lib/supabase/queries/useAdherenciaPaciente';
import { useEvolucionPaciente } from '@/lib/paciente/useEvolucionPaciente';
import { useCurrentPacienteId } from '@/app/components/auth/AuthProvider';
import { EvolucionChart } from '@/app/components/charts/EvolucionChart';

export function ProgresoPaciente() {
  const progreso = useProgresoPaciente();
  const evolucion = useEvolucionPaciente(useCurrentPacienteId());
  const rom = evolucion.data?.rom ?? null;
  const dolorSemanas = evolucion.data?.dolorSemanas ?? [];

  return (
    <div className="min-h-screen w-full flex flex-col bg-background transition-colors duration-200">
      <PacienteHeaderDesktop />

      <div className="flex-1 flex justify-center lg:justify-start">
        <PacienteSidebar />

        <div className="w-full max-w-[480px] lg:max-w-none flex flex-col pb-24 lg:pb-8 lg:flex-1 lg:min-w-0">
          <div className="p-6 lg:px-8 lg:pt-6 space-y-6">
            {/* Header mobile con ChevronLeft. Desktop lo cubre PacienteHeaderDesktop. */}
            <div className="flex items-center gap-4 lg:hidden">
              <Link
                to="/paciente-home"
                className="transition-all duration-200 ease-out hover:opacity-70"
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
              </Link>
              <h1 className="text-2xl">Tu progreso</h1>
            </div>

            <div className="hidden lg:flex items-baseline justify-between">
              <h1 className="text-2xl font-bold text-primary">Tu progreso</h1>
            </div>

            {/* Stat cards con data REAL desde sesión 1 (Q4 ratificado en LOCK). */}
            <StatsRow progreso={progreso} />

            {/* Weekly completions strip con data real de los últimos 7 días. */}
            <WeeklyStrip progreso={progreso} />

            {/* ROM (pose_analysis) y Dolor (dolor_reportado) por semana. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EvolucionChart
                titulo="Rango de movimiento"
                subtitulo={rom ? `${rom.ejercicioNombre} · grados` : "Grados por semana"}
                data={rom?.semanas ?? []}
                unidad="°"
                color="var(--chart-2)"
                destacado={
                  rom
                    ? {
                        valor: `${rom.ganado >= 0 ? "+" : ""}${rom.ganado}°`,
                        label: "ganados",
                        tono: rom.ganado > 0 ? "bueno" : "neutro",
                      }
                    : undefined
                }
              />
              <EvolucionChart
                titulo="Nivel de dolor"
                subtitulo="Promedio semanal · escala 0-10"
                data={dolorSemanas}
                unidad="/10"
                color="var(--chart-4)"
                dominio={[0, 10]}
                destacado={
                  dolorSemanas.length > 0
                    ? {
                        valor: `${dolorSemanas[dolorSemanas.length - 1].value}/10`,
                        label: "esta semana",
                      }
                    : undefined
                }
                vacio="Todavía no reportaste dolor en tus sesiones"
              />
            </div>
          </div>
        </div>
      </div>

      <PacienteBottomNav />
    </div>
  );
}

// ─── Stats cards ──────────────────────────────────────────────────────────

function StatsRow({ progreso }: { progreso: ProgresoPacienteData }) {
  const { adherencia, adherenciaLoading, sesionesTotal, rachaDias, loading } =
    progreso;

  return (
    <div className="grid grid-cols-3 gap-3">
      <StatCard
        icon={<Flame className="w-5 h-5 text-success" strokeWidth={1.5} />}
        iconBg="bg-success/20"
        value={loading ? null : rachaDias}
        label="Días seguidos"
      />
      <AdherenciaStatCard
        adherencia={adherencia}
        loading={adherenciaLoading}
      />
      <StatCard
        icon={<Trophy className="w-5 h-5 text-accent" strokeWidth={1.5} />}
        iconBg="bg-accent/20"
        value={loading ? null : sesionesTotal}
        label="Sesiones"
      />
    </div>
  );
}

function AdherenciaStatCard({
  adherencia,
  loading,
}: {
  adherencia: AdherenciaResult | null;
  loading: boolean;
}) {
  const { value, hint } = adherenciaDisplay(adherencia);
  return (
    <div className="relative bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center justify-center w-10 h-10 bg-primary/20 rounded-full mb-3">
        <Target className="w-5 h-5 text-primary" strokeWidth={1.5} />
      </div>
      {loading ? (
        <div className="h-8 w-16 bg-muted rounded animate-pulse" />
      ) : (
        <div className="text-2xl font-[800] tabular-nums">{value}</div>
      )}
      <p className="text-xs text-muted-foreground mt-1">Adherencia</p>
      {hint && !loading && (
        <p className="text-[10px] text-muted-foreground/70 mt-0.5 leading-tight">
          {hint}
        </p>
      )}
    </div>
  );
}

/** Display de la stat "Adherencia" para el paciente. Copies levemente
 *  distintos que el kine (más informal, primera persona). */
function adherenciaDisplay(data: AdherenciaResult | null): {
  value: string;
  hint: string;
} {
  if (!data) return { value: '—', hint: '' };
  if (data.code === 'NO_AUTH' || data.code === 'NOT_FOUND')
    return { value: '—', hint: '' };
  if (data.code === 'UNKNOWN')
    return { value: '—', hint: '' };
  if (data.code === 'NO_PLAN_ACTIVO')
    return { value: '—', hint: 'Sin plan activo' };
  if (data.has_frecuencia === false)
    return {
      value: '—',
      hint: 'Pedile al kine que configure la frecuencia semanal',
    };
  if (data.code === 'VENTANA_MUY_CORTA')
    return {
      value: '—',
      hint: 'Plan recién iniciado, todavía sin datos de adherencia',
    };
  if (typeof data.adherencia_pct === 'number')
    return { value: `${data.adherencia_pct}%`, hint: '' };
  return { value: '—', hint: '' };
}

function StatCard({
  icon,
  iconBg,
  value,
  label,
}: {
  icon: React.ReactNode;
  iconBg: string;
  value: number | string | null;
  label: string;
}) {
  return (
    <div className="relative bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className={`flex items-center justify-center w-10 h-10 ${iconBg} rounded-full mb-3`}>
        {icon}
      </div>
      {value === null ? (
        <div className="h-8 w-12 bg-muted rounded animate-pulse" />
      ) : (
        <div className="text-2xl font-[800] tabular-nums">{value}</div>
      )}
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

// ─── Weekly strip ─────────────────────────────────────────────────────────

function WeeklyStrip({ progreso }: { progreso: ProgresoPacienteData }) {
  const { weekCompletions, loading } = progreso;
  const weekCompletados = weekCompletions.filter((d) => d.completed).length;
  const weekTotal = weekCompletions.length;

  return (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg">Esta semana</h2>
        <span className="text-sm text-muted-foreground">
          {loading ? '—' : `${weekCompletados} de ${weekTotal} completadas`}
        </span>
      </div>
      <div className="flex justify-between mb-4">
        {weekCompletions.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center gap-2">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-200 ${
                item.completed
                  ? 'bg-success text-success-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {item.completed && '✓'}
            </div>
            <span className="text-xs text-muted-foreground">{item.day}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
